import { resolveSiteUrls } from '../../../shared/site-urls';
import { whatsappLink } from '../../../shared/contact';
import { resolveDataSource } from './data-source';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  anyPhoneInstallUrl,
  appPairUrl,
  phonePlatform,
  playInstallUrl,
  storeInstallUrl,
} from '../../shared/install-link';

const env = import.meta.env;

const demoSwitchEnabled = env.VITE_ENABLE_DEMO_SWITCH === 'true';
const dataSource = resolveDataSource({
  switchEnabled: demoSwitchEnabled,
  search: typeof window === 'undefined' ? '' : window.location.search,
});

const siteUrls = resolveSiteUrls({ marketing: env.VITE_MARKETING_URL, help: env.VITE_HELP_URL });
const marketingUrl = siteUrls.marketing;

export const config = {
  supabaseUrl: env.VITE_SUPABASE_URL || '',
  supabaseAnonKey: env.VITE_SUPABASE_ANON_KEY || '',
  razorpayKeyId: env.VITE_RAZORPAY_KEY_ID || '',
  paddleEnabled: env.VITE_PADDLE_ENABLED === 'true',
  googleClientId: env.VITE_GOOGLE_CLIENT_ID || '',
  marketingUrl,
  helpUrl: siteUrls.help,
  apiBase: (env.VITE_API_BASE || '').replace(/\/+$/, ''),
  recordingsApi: (env.VITE_RECORDINGS_API || '').replace(/\/+$/, ''),
  mcpApi: (env.VITE_MCP_API || 'https://mcp.callsyncapp.com').replace(/\/+$/, ''),
  /** The WhatsApp worker: notification settings, signup leads and the support chat. */
  communicationApi: (env.VITE_COMMUNICATION_API || 'https://callsync-whatsapp-marketing.ygsbhardwaj.workers.dev').replace(/\/+$/, ''),
  oauthServerEnabled: env.VITE_OAUTH_SERVER_ENABLED === 'true',
  analyticsEnabled: env.VITE_ENABLE_ANALYTICS === 'true',
  demoSwitchEnabled,
  dataSource,
  isDemo: dataSource === 'demo',
  isSandbox: env.VITE_BACKEND_ENV === 'sandbox' && dataSource === 'supabase',
};

/**
 * The dashboard lives on its own subdomain; everything marketing-owned
 * (legal pages, staff setup guide, webhook guide) is on the main site, so
 * those links are always absolute.
 */
export const links = {
  marketing: marketingUrl,
  pricing: `${marketingUrl}/pricing`,
  privacy: `${marketingUrl}/privacy`,
  terms: `${marketingUrl}/terms`,
  refund: `${marketingUrl}/refund`,
  support: `${marketingUrl}/contact/`,
  deleteData: `${marketingUrl}/delete-data`,
  webhookGuide: `${siteUrls.help}/webhook-guide/`,
  setupGuide: (code?: string | null) =>
    code
      ? `${siteUrls.help}/employee-app-setup/?code=${encodeURIComponent(code)}`
      : `${siteUrls.help}/employee-app-setup/`,
  playStore: PLAY_STORE_URL,
  /** Play link carrying the pairing code through the install referrer. */
  playInstall: playInstallUrl,
  /** The iPhone staff app. iOS has no install referrer, so the person types the code. */
  appStore: APP_STORE_URL,
  /** The one install button: the App Store on an iPhone, otherwise Google Play with the code. */
  storeInstall: (code?: string | null) => storeInstallUrl(thisPhone, code),
  /** For a QR code or a message whose reader's phone isn't known: the right store for whoever opens it. */
  anyPhoneInstall: (code?: string | null) => anyPhoneInstallUrl(code, window.location.origin),
  appPair: appPairUrl,
  /** Served with the dashboard, so it works wherever the dashboard is deployed. */
  phoneSetupVideo: () => `${window.location.origin}/media/callsync-android-setup-en.mp4`,
  /** Free CallSync Recorder download page with its install guide; the recorder is not on Google Play. */
  recorderDownload: `${marketingUrl}/call-recorder-app/download/`,
  /** Help page comparing the ways a phone can record, including sharing from Phone by Google. */
  recordingOptions: `${siteUrls.help}/recording-options/`,
  supportEmail: 'support@callsyncapp.com',
  whatsappSupport: (text: string) => whatsappLink(text),
};

/** The phone this dashboard is open on, if it is one; decides which store comes first. */
export const thisPhone =
  typeof navigator === 'undefined' ? null : phonePlatform(navigator.userAgent, navigator.maxTouchPoints);
